import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import './ContractorPages.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const isFreshNotification = (createdAt) => {
  const ts = new Date(createdAt).getTime();
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts <= 12 * 60 * 60 * 1000;
};

const ContractorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  const [latestNotification, setLatestNotification] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const loadDashboard = async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
          setError('');
        }
        const [dashRes, entriesRes] = await Promise.all([
          API.get('/contractor/dashboard'),
          API.get('/contractor/work-entries'),
        ]);
        if (!active) return;
        setData(dashRes.data || null);
        setAllEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      } catch (e) {
        if (!silent && active) {
          setError(e?.response?.data?.message || 'Failed to load dashboard');
        }
      } finally {
        if (!silent && active) {
          setLoading(false);
        }
      }
    };

    const loadNotifications = async () => {
      try {
        const res = await API.get('/contractor/notifications', { params: { limit: 1 } });
        if (!active) return;
        const items = Array.isArray(res.data) ? res.data : [];
        setLatestNotification(items[0] || null);
      } catch {
        if (active) {
          setLatestNotification(null);
        }
      }
    };

    const hydrate = async () => {
      await Promise.all([loadDashboard(false), loadNotifications()]);
    };

    hydrate();

    const intervalId = window.setInterval(() => {
      loadDashboard(true);
      loadNotifications();
    }, 15000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const summary = data?.summary || {};
  const profile = data?.profile || null;
  const rates = Array.isArray(data?.rates) ? data.rates : [];

  const completionScore = useMemo(() => {
    if (!profile) return 0;
    const has = (v) => String(v || '').trim().length > 0;
    const checks = [
      has(profile.contractorName),
      has(profile.millName),
      has(profile.contactPerson),
      has(profile.contactMobile),
      has(profile.address),
      has(profile.notes),
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [profile]);

  const analytics = useMemo(() => {
    const rows = Array.isArray(allEntries) ? [...allEntries] : [];
    rows.sort((a, b) => new Date(a.workDate || a.createdAt) - new Date(b.workDate || b.createdAt));

    const byWorkType = rows.reduce((acc, r) => {
      const k = r.workType || 'Unknown';
      acc[k] = (acc[k] || 0) + Number(r.totalAmount || 0);
      return acc;
    }, {});

    const byDay = rows.reduce((acc, r) => {
      const dt = new Date(r.workDate || r.createdAt);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      acc[key] = (acc[key] || 0) + Number(r.totalAmount || 0);
      return acc;
    }, {});

    const byWorker = rows.reduce((acc, r) => {
      const k = r.workerName || 'Unknown';
      acc[k] = {
        amount: (acc[k]?.amount || 0) + Number(r.totalAmount || 0),
        jobs: (acc[k]?.jobs || 0) + 1,
      };
      return acc;
    }, {});

    const topWorkers = Object.entries(byWorker)
      .map(([name, stat]) => ({ name, ...stat }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);

    const uniqueWorkers = new Set(rows.map((r) => String(r.workerName || '').trim()).filter(Boolean)).size;
    const avgTicket = rows.length ? rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0) / rows.length : 0;

    const trendKeys = Object.keys(byDay).sort().slice(-10);
    const trendLabels = trendKeys.map((d) => {
      const [, mm, dd] = d.split('-');
      return `${dd}/${mm}`;
    });

    return {
      byWorkType,
      trendKeys,
      trendLabels,
      byDay,
      topWorkers,
      uniqueWorkers,
      avgTicket,
    };
  }, [allEntries]);

  const topWorkTypes = useMemo(
    () =>
      Object.entries(analytics.byWorkType)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 4),
    [analytics.byWorkType]
  );

  const paymentSummary = useMemo(() => {
    const rows = Array.isArray(allEntries) ? allEntries : [];
    const todayKey = new Date().toDateString();
    const todayRows = rows.filter((item) => new Date(item.workDate || item.createdAt).toDateString() === todayKey);
    const pendingApprovalAmount = rows
      .filter((item) => String(item.workflowStatus || '') !== 'plant_approved')
      .reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    const approvedAmount = rows
      .filter((item) => String(item.workflowStatus || '') === 'plant_approved')
      .reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    const todayPending = todayRows.some((item) => String(item.workflowStatus || '') !== 'plant_approved');
    const todayRejected = todayRows.length > 0 && todayRows.every((item) => String(item.workflowStatus || '') === 'plant_rejected');

    return {
      todayPending,
      todayRejected,
      pendingApprovalAmount,
      approvedAmount,
    };
  }, [allEntries]);

  const workTypeChartData = useMemo(() => {
    const labels = Object.keys(analytics.byWorkType);
    const values = labels.map((k) => analytics.byWorkType[k]);
    return {
      labels,
      datasets: [
        {
          label: 'Amount by Work Type',
          data: values,
          backgroundColor: ['#f59e0b', '#22c55e', '#0ea5e9', '#ef4444', '#8b5cf6', '#14b8a6'],
          borderWidth: 0,
        },
      ],
    };
  }, [analytics.byWorkType]);

  const trendChartData = useMemo(() => ({
    labels: analytics.trendLabels,
    datasets: [
      {
        label: 'Daily Amount',
        data: analytics.trendKeys.map((k) => analytics.byDay[k] || 0),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37,99,235,0.14)',
        fill: true,
        tension: 0.35,
        pointRadius: 3,
      },
    ],
  }), [analytics.trendLabels, analytics.trendKeys, analytics.byDay]);

  const profileHighlights = [
    { label: 'Contact Person', value: profile?.contactPerson || '—' },
    { label: 'Mobile', value: profile?.contactMobile || '—' },
    { label: 'Address', value: profile?.address || '—', wide: true },
    { label: 'Last Entry', value: data?.recentEntries?.[0]?.workDate ? fmtDate(data.recentEntries[0].workDate) : '—' },
  ];

  const quickActions = [
    {
      title: 'Add New Work',
      text: 'Aaj ka kaam, worker aur quantity turant save karo.',
      icon: 'edit_note',
      tone: 'primary',
      action: () => navigate('/contractor/work-entry'),
      cta: 'Add Work',
    },
    {
      title: 'Check History',
      text: 'Purani entries aur approval flow ek jagah dekho.',
      icon: 'history',
      tone: 'warm',
      action: () => navigate('/contractor/work-history'),
      cta: 'View History',
    },
    {
      title: 'Payment Ledger',
      text: 'Jo amount approve ho gaya hai uska payment status track karo.',
      icon: 'receipt_long',
      tone: 'cool',
      action: () => navigate('/contractor/payment-history'),
      cta: 'Open Payments',
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="contractor-page">
      {error && <Alert variant="danger">{error}</Alert>}
      {latestNotification && (
        <section className={`contractor-live-alert ${isFreshNotification(latestNotification.createdAt) ? 'fresh' : ''}`}>
          <div className="contractor-live-alert-icon material-symbols-outlined">campaign</div>
          <div className="contractor-live-alert-copy">
            <div className="contractor-live-alert-topline">
              <span className="contractor-live-alert-label">Live Update</span>
              {isFreshNotification(latestNotification.createdAt) ? (
                <span className="contractor-inline-pill warning">New</span>
              ) : null}
            </div>
            <strong>{latestNotification.message}</strong>
            <div className="contractor-live-alert-time">{fmtDate(latestNotification.createdAt)}</div>
          </div>
        </section>
      )}
      {!profile && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>Profile complete karo, tab dashboard insights aur better ho jayenge.</span>
          <Button size="sm" onClick={() => navigate('/contractor/profile')}>Complete Profile</Button>
        </Alert>
      )}

      <section className="contractor-hero contractor-hero-premium">
        <div className="contractor-hero-main">
          <div className="contractor-hero-badge">
            {(profile?.contractorName || 'Contractor')
              .split(' ')
              .map((part) => part[0] || '')
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="contractor-hero-copy">
            <div className="contractor-hero-title-row">
              <h3>{profile?.contractorName || 'Contractor Dashboard'}</h3>
              <span className={`contractor-hero-chip ${paymentSummary.todayPending ? 'pending' : 'approved'}`}>
                {paymentSummary.todayPending ? (paymentSummary.todayRejected ? 'Rejected Today' : 'Pending Today') : 'Approved Flow'}
              </span>
            </div>
            <div className="contractor-hero-sub">{profile?.millName || 'Mill name not added yet'}</div>
            <div className="contractor-hero-meta">
              <span>Today entries: {summary.todayEntries || 0}</span>
              <span>Approved total: {money(paymentSummary.approvedAmount)}</span>
              <span>Pending total: {money(paymentSummary.pendingApprovalAmount)}</span>
            </div>
          </div>
        </div>
        <div className="contractor-hero-scorecard">
          <div className="contractor-score-label">Profile Score</div>
          <div className="contractor-score-value">{completionScore}<small>/100</small></div>
          <Button variant="light" size="sm" onClick={() => navigate('/contractor/profile')}>Edit Profile</Button>
        </div>
      </section>

      <section className="contractor-grid contractor-grid-kpi">
        <div className="contractor-kpi">
          <div className="label">Today Entries</div>
          <div className="value">{summary.todayEntries || 0}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Today Amount</div>
          <div className="value">{money(summary.todayAmount)}</div>
          {paymentSummary.todayPending ? (
            <div className="contractor-kpi-foot">
              <span className={`contractor-inline-pill ${paymentSummary.todayRejected ? 'danger' : 'warning'}`}>
                {paymentSummary.todayRejected ? 'Rejected' : 'Pending'}
              </span>
            </div>
          ) : null}
        </div>
        <div className="contractor-kpi">
          <div className="label">Total Entries</div>
          <div className="value">{summary.totalEntries || 0}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Total Amount</div>
          <div className="value">{money(paymentSummary.approvedAmount)}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Unique Workers</div>
          <div className="value">{analytics.uniqueWorkers}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Avg Ticket</div>
          <div className="value">{money(analytics.avgTicket)}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Payment Pending</div>
          <div className="value">{money(paymentSummary.pendingApprovalAmount)}</div>
        </div>
        <div className="contractor-kpi">
          <div className="label">Approved for Payment</div>
          <div className="value">{money(paymentSummary.approvedAmount)}</div>
        </div>
        <div className="contractor-kpi contractor-kpi-highlight">
          <div className="label">Profile Score</div>
          <div className="value">{completionScore}/100</div>
        </div>
      </section>

      <section className="contractor-quick-actions">
        {quickActions.map((item) => (
          <button key={item.title} type="button" className={`contractor-action-card ${item.tone}`} onClick={item.action}>
            <div className="contractor-action-icon material-symbols-outlined">{item.icon}</div>
            <div className="contractor-action-copy">
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </div>
            <div className="contractor-action-cta">{item.cta}</div>
          </button>
        ))}
      </section>

      <section className="contractor-grid contractor-grid-charts contractor-grid-charts-premium">
        <div className="contractor-card contractor-chart-card">
          <div className="contractor-card-head">
            <h5>Last 10 Days Trend</h5>
            <span className="contractor-card-sub">Day-wise work value</span>
          </div>
          {analytics.trendKeys.length ? (
            <Line
              data={trendChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } },
              }}
            />
          ) : <div className="text-muted">No trend data available.</div>}
        </div>

        <div className="contractor-card contractor-chart-card contractor-chart-doughnut">
          <div className="contractor-card-head">
            <h5>Work Type Split</h5>
            <span className="contractor-card-sub">Kaun sa kaam sabse zyada hua</span>
          </div>
          {Object.keys(analytics.byWorkType).length ? (
            <Doughnut
              data={workTypeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } },
              }}
            />
          ) : <div className="text-muted">No work type data.</div>}
        </div>

        <div className="contractor-card contractor-spotlight-card">
          <div className="contractor-card-head">
            <h5>Top Workers</h5>
            <span className="contractor-card-sub">Amount contribution</span>
          </div>
          {analytics.topWorkers.length ? (
            <div className="contractor-rank-list">
              {analytics.topWorkers.map((worker, index) => (
                <div className="contractor-rank-item" key={worker.name}>
                  <div className="contractor-rank-badge">{index + 1}</div>
                  <div className="contractor-rank-copy">
                    <strong>{worker.name}</strong>
                    <span>{worker.jobs} entries</span>
                  </div>
                  <div className="contractor-rank-value">{money(worker.amount)}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-muted">No worker data.</div>}
        </div>
      </section>

      <section className="contractor-grid contractor-grid-two contractor-grid-two-premium">
        <div className="contractor-card">
          <div className="contractor-card-head">
            <h5>Contractor Profile Snapshot</h5>
            <Button variant="link" onClick={() => navigate('/contractor/profile')}>Manage</Button>
          </div>
          <div className="contractor-details-grid contractor-details-grid-premium">
            <div className="contractor-detail-tile">
              <div className="contractor-field-label">Contractor Name</div>
              <div className="contractor-field-value">{profile?.contractorName || '—'}</div>
            </div>
            <div className="contractor-detail-tile">
              <div className="contractor-field-label">Mill Name</div>
              <div className="contractor-field-value">{profile?.millName || '—'}</div>
            </div>
            {profileHighlights.map((item) => (
              <div key={item.label} className={`contractor-detail-tile ${item.wide ? 'contractor-span-2' : ''}`}>
                <div className="contractor-field-label">{item.label}</div>
                <div className="contractor-field-value">{item.value}</div>
              </div>
            ))}
            <div className="contractor-detail-tile contractor-span-2">
              <div className="contractor-field-label">Notes</div>
              <div className="contractor-field-value">{profile?.notes || '—'}</div>
            </div>
          </div>
        </div>

        <div className="contractor-card">
          <div className="contractor-card-head">
            <h5>Rates & Work Mix</h5>
            <span className="contractor-card-sub">Admin configured pricing board</span>
          </div>
          {rates.length === 0 && topWorkTypes.length === 0 ? (
            <div className="text-muted">Admin has not configured rates yet.</div>
          ) : (
            <div className="contractor-rate-board">
              <div className="contractor-rate-list">
                {rates.slice(0, 5).map((r) => (
                  <div className="contractor-rate-item" key={r._id}>
                    <div>
                      <strong>{r.workType}</strong>
                      <span>{r.unit || 'job'}</span>
                    </div>
                    <div className="contractor-rate-value">{money(r.rate)}</div>
                  </div>
                ))}
              </div>
              <div className="contractor-mix-list">
                {topWorkTypes.map((item) => (
                  <div className="contractor-mix-item" key={item.name}>
                    <span>{item.name}</span>
                    <strong>{money(item.amount)}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContractorDashboard;
